import * as React from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';

const switchClasses =
  'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-surface-button-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-accent data-[state=unchecked]:bg-stroke';

const switchThumbClasses =
  'pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0 data-[state=unchecked]:bg-navigation-element';

const switchLabelClasses = 'text-sm leading-5 font-normal';

interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: string;
  htmlFor?: string;
  description?: string;
  variant?: 'default' | 'description' | 'choice-card';
  destructive?: boolean;
  switchPosition?: 'left' | 'right';
  tooltip?: React.ReactNode;
  tooltipSide?: React.ComponentProps<typeof InfoTooltip>['side'];
}

const Switch = ({
  className,
  label,
  htmlFor,
  description,
  variant = 'default',
  destructive = false,
  switchPosition,
  checked,
  disabled,
  tooltip,
  tooltipSide,
  ref,
  ...props
}: SwitchProps & { ref?: React.Ref<React.ElementRef<typeof SwitchPrimitive.Root>> }) => {
  const autoId = React.useId();
  const switchId = props.id ?? htmlFor ?? autoId;
  const resolvedPosition = switchPosition ?? (variant === 'default' ? 'right' : 'left');

  const labelColor = destructive ? 'text-warning' : 'text-foreground';

  const toggle = (
    <SwitchPrimitive.Root
      {...props}
      id={switchId}
      ref={ref}
      data-slot="switch"
      checked={checked}
      disabled={disabled}
      className={cn(switchClasses, tooltip && 'disabled:cursor-default')}
    >
      <SwitchPrimitive.Thumb className={switchThumbClasses} />
    </SwitchPrimitive.Root>
  );

  const switchToggle = tooltip ? (
    <InfoTooltip content={tooltip} side={tooltipSide}>
      <span className="pointer-events-auto inline-flex">{toggle}</span>
    </InfoTooltip>
  ) : (
    toggle
  );

  if (variant === 'choice-card') {
    const content = (
      <div className="flex flex-1 flex-col gap-1">
        {label && (
          <span className={cn('text-sm leading-none font-medium', labelColor)}>{label}</span>
        )}
        {description && (
          <span className="text-sm leading-5 font-normal text-foreground-muted">{description}</span>
        )}
      </div>
    );

    return (
      <label
        htmlFor={switchId}
        className={cn(
          'flex items-start gap-4 rounded-lg border border-stroke p-4 transition-colors',
          !disabled && 'cursor-pointer',
          checked && 'bg-background',
          className
        )}
      >
        {resolvedPosition === 'left' ? (
          <>
            {switchToggle}
            {content}
          </>
        ) : (
          <>
            {content}
            {switchToggle}
          </>
        )}
      </label>
    );
  }

  if (variant === 'description') {
    const content = (
      <div className="flex flex-1 flex-col gap-1">
        {label && (
          <label htmlFor={switchId} className={cn('text-sm leading-none font-medium', labelColor)}>
            {label}
          </label>
        )}
        {description && (
          <span className="text-sm leading-5 font-normal text-foreground-muted">{description}</span>
        )}
      </div>
    );

    return (
      <div
        className={cn(
          'flex items-start gap-4',
          disabled && 'pointer-events-none cursor-not-allowed opacity-50',
          className
        )}
      >
        {resolvedPosition === 'left' ? (
          <>
            {switchToggle}
            {content}
          </>
        ) : (
          <>
            {content}
            {switchToggle}
          </>
        )}
      </div>
    );
  }

  // Default variant — backward compatible
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {resolvedPosition === 'left' && switchToggle}
      {label && (
        <label className={cn(switchLabelClasses, labelColor)} htmlFor={switchId}>
          {label}
        </label>
      )}
      {resolvedPosition === 'right' && switchToggle}
    </div>
  );
};

export { Switch };
